"use client";
import React, { createContext, useContext, useEffect, useState } from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  signInWithPhoneNumber,
  RecaptchaVerifier,
  type ConfirmationResult,
  type User,
} from "firebase/auth";
import { Timestamp } from "firebase/firestore";
import { auth } from "@/firebase/config";
import { getUserProfile, createOrUpdateUserProfile, subscribeToUserProfile } from "@/firebase/firestoreServices";
import type { UserProfile } from "@/models";
import { type Unsubscribe } from "firebase/firestore";

interface AuthContextType {
  user: User | null;
  profile: UserProfile | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, name: string) => Promise<void>;
  logout: () => Promise<void>;
  sendOtp: (phone: string, containerId: string) => Promise<ConfirmationResult>;
  verifyOtp: (confirmation: ConfirmationResult, otp: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  profile: null,
  loading: true,
  login: async () => { },
  signup: async () => { },
  logout: async () => { },
  sendOtp: async () => { throw new Error("Not implemented"); },
  verifyOtp: async () => { },
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsubscribeProfile: Unsubscribe | null = null;

    const unsubscribeAuth = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);

      // Clean up previous profile subscription
      if (unsubscribeProfile) {
        unsubscribeProfile();
        unsubscribeProfile = null;
      }

      if (firebaseUser) {
        unsubscribeProfile = subscribeToUserProfile(firebaseUser.uid, (p) => {
          setProfile(p);
        });
      } else {
        setProfile(null);
      }
      setLoading(false);
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeProfile) unsubscribeProfile();
    };
  }, []);

  const login = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  const signup = async (email: string, password: string, name: string) => {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    await createOrUpdateUserProfile(cred.user.uid, {
      name,
      email,
      rewardPoints: 0,
      createdAt: Timestamp.now(),
    });
  };

  const logout = async () => {
    await signOut(auth);
    setProfile(null);
  };

  const sendOtp = async (phone: string, containerId: string): Promise<ConfirmationResult> => {
    const win = window as Window & { __recaptchaVerifier?: RecaptchaVerifier };
    if (win.__recaptchaVerifier) {
      try { win.__recaptchaVerifier.clear(); } catch { /* already cleared */ }
      win.__recaptchaVerifier = undefined;
    }
    const verifier = new RecaptchaVerifier(auth, containerId, { size: "invisible" });
    win.__recaptchaVerifier = verifier;
    return await signInWithPhoneNumber(auth, phone, verifier);
  };

  const verifyOtp = async (confirmation: ConfirmationResult, otp: string): Promise<void> => {
    const cred = await confirmation.confirm(otp);
    const existing = await getUserProfile(cred.user.uid);
    if (!existing) {
      await createOrUpdateUserProfile(cred.user.uid, {
        name: "",
        phone: cred.user.phoneNumber || "",
        number: cred.user.phoneNumber || "",
        rewardPoints: 0,
        createdAt: Timestamp.now(),
      });
    }
  };

  return (
    <AuthContext.Provider value={{ user, profile, loading, login, signup, logout, sendOtp, verifyOtp }}>
      {children}
    </AuthContext.Provider>
  );
};
